import { atom } from 'jotai';
import type { Getter, Setter } from 'jotai/vanilla';
import { isClassificationAnnotationId } from '../../../../common/review-mode-side-panel/utils';
import { getLastInstanceCount, getRandomRGBColor } from '../../../../common/side-panel/segmentation-labels/utils';
import { type AnnotationLabelT } from '../../../../common/types/types';
import { SIDE_PANEL_ACTIONS } from '../../../../modernization-common/side-panel/const';
import type { ModernizedGenericAnnotation, SidePanelActions } from '../../../../modernization-common/side-panel/types';
import { deleteAnnotationAction, deleteAnnotationsAction, importMLAnnotationsAction } from '../../../labelling/reducers/actions/annotation';
import { deleteAnnotationFrameSpan } from '../../../labelling/reducers/actions/video';
import { annotationLabelsAtom, isVideoTaskAtom } from '../../atoms/annotation-atoms';
import { dispatchAtom } from '../../atoms/label-store-dispatch/dispatch';
import { EVENTS, UNDOABLE_EVENTS } from '../../atoms/label-store-dispatch/events';
import { annotationFrameSpanAtom, annotationIdToSpanAtom, selectedFilterAtom, videoFilterAtom } from '../../atoms/side-panel-atoms';
import { currentFrameAtom } from '../../atoms/video-playback-atoms';
import { VIDEO_FILTERS } from '../../constants';
type EditAnnotationStartActionT = Extract<SidePanelActions<ModernizedGenericAnnotation>, {
  type: typeof SIDE_PANEL_ACTIONS.EDIT_ANNOTATION_START;
}>;
type EditAnnotationEndActionT = Extract<SidePanelActions<ModernizedGenericAnnotation>, {
  type: typeof SIDE_PANEL_ACTIONS.EDIT_ANNOTATION_END;
}>;
type DispatchExtensionOptionsT = {
  onEditAnnotationStart?: (params: {
    get: Getter;
    set: Setter;
    action: EditAnnotationStartActionT;
  }) => void;
  onEditAnnotationEnd?: (params: {
    get: Getter;
    set: Setter;
    action: EditAnnotationEndActionT;
  }) => void;
  mapSelectedAnnotationId?: (annotationId: string) => string | null;
};
const getFrameRangeForAnnotation = (get: Getter, annotationId: string): [number, number][] | undefined => {
  const annotationIdToSpanMap = get(annotationIdToSpanAtom);
  const frameRanges = annotationIdToSpanMap[annotationId];
  return frameRanges?.map(({
    start,
    end
  }) => [start, end]);
};
export const createSidePanelDispatchAtom = ({
  onEditAnnotationStart,
  onEditAnnotationEnd,
  mapSelectedAnnotationId
}: DispatchExtensionOptionsT = {}) => atom(undefined, (get, set, action: SidePanelActions<ModernizedGenericAnnotation>) => {
  const currentFrame = get(currentFrameAtom);
  switch (action.type) {
    case SIDE_PANEL_ACTIONS.UPDATE_ANNOTATION:
      {
        return;
      }
    case SIDE_PANEL_ACTIONS.UPDATE_ANNOTATION_LABEL:
      {
        set(dispatchAtom, {
          type: UNDOABLE_EVENTS.UPDATE_LABEL_FOR_ANNOTATION,
          annotationIdx: action.annotationIdx,
          annotationLabel: action.annotationLabel
        });
        return;
      }
    case SIDE_PANEL_ACTIONS.UPDATE_ANNOTATION_QUESTIONNAIRE:
      {
        set(dispatchAtom, {
          type: EVENTS.UPDATE_QUESTIONNAIRE,
          annotationId: action.annotationIdx,
          answers: action.answers
        });
        return;
      }
    case SIDE_PANEL_ACTIONS.DELETE_ANNOTATION:
      {
        set(dispatchAtom, deleteAnnotationAction(currentFrame, action.annotationIdx));
        return;
      }
    case SIDE_PANEL_ACTIONS.DELETE_ANNOTATIONS:
      {
        const videoFilter = get(videoFilterAtom);
        const isCurrentFrameFilter = videoFilter === VIDEO_FILTERS.CURRENT_FRAME;
        return set(dispatchAtom, deleteAnnotationsAction({
          annotationLabels: action.annotationLabels,
          ...(isCurrentFrameFilter && {
            currentFrame
          })
        }));
      }
    case SIDE_PANEL_ACTIONS.SHOW_HIDE_ANNOTATIONS:
      {
        const isVideoTask = get(isVideoTaskAtom);
        const videoFilter = get(videoFilterAtom);
        const isCurrentFrameFilter = isVideoTask ? videoFilter === VIDEO_FILTERS.CURRENT_FRAME : true;
        const annotationFrameRange = action.annotationId && !isCurrentFrameFilter ? getFrameRangeForAnnotation(get, action.annotationId) : undefined;
        set(dispatchAtom, {
          type: EVENTS.SHOW_HIDE_ANNOTATIONS,
          annotationId: action.annotationId,
          hidden: action.hidden,
          annotationLabel: action.annotationLabel,
          allAnnotations: action.allAnnotations,
          storeType: action.storeType,
          ...(isCurrentFrameFilter && {
            frameIndex: currentFrame
          }),
          ...(annotationFrameRange && {
            frameRange: annotationFrameRange
          })
        });
        return;
      }
    case SIDE_PANEL_ACTIONS.LOCK_ANNOTATIONS:
      {
        const videoFilter = get(videoFilterAtom);
        const isCurrentFrameFilter = videoFilter === VIDEO_FILTERS.CURRENT_FRAME;
        const annotationFrameRange = action.annotationId && !isCurrentFrameFilter ? getFrameRangeForAnnotation(get, action.annotationId) : undefined;
        set(dispatchAtom, {
          type: EVENTS.LOCK_ANNOTATIONS,
          annotationId: action.annotationId,
          locked: action.locked,
          ...(isCurrentFrameFilter && {
            frameIndex: currentFrame
          }),
          ...(annotationFrameRange && {
            frameRange: annotationFrameRange
          })
        });
        return;
      }
    case SIDE_PANEL_ACTIONS.EDIT_ANNOTATION_START:
      {
        set(dispatchAtom, {
          type: EVENTS.SELECT_ANNOTATION,
          annotationId: action.annotationId
        });
        set(dispatchAtom, {
          type: EVENTS.OPEN_ANNOTATION_DETAILS_PANEL
        });
        onEditAnnotationStart?.({
          get,
          set,
          action
        });
        return;
      }
    case SIDE_PANEL_ACTIONS.EDIT_ANNOTATION_END:
      {
        set(dispatchAtom, {
          type: EVENTS.HIDE_ANNOTATION_DETAILS_PANEL
        });
        onEditAnnotationEnd?.({
          get,
          set,
          action
        });
        return;
      }
    case SIDE_PANEL_ACTIONS.SET_FOCUSED_ANNOTATION:
      {
        set(dispatchAtom, {
          type: EVENTS.FOCUS_ANNOTATION,
          annotationId: action.annotationId
        });
        return;
      }
    case SIDE_PANEL_ACTIONS.SELECT_ANNOTATION:
      {
        const isVideoTask = get(isVideoTaskAtom);
        if (isVideoTask) {
          const annotationFrameSpan = get(annotationFrameSpanAtom);
          const span = annotationFrameSpan[action.annotationId];
          if (!span) {
            if (isClassificationAnnotationId(action.annotationId)) {
              set(dispatchAtom, {
                type: EVENTS.SELECT_ANNOTATION,
                annotationId: mapSelectedAnnotationId ? mapSelectedAnnotationId(action.annotationId) : action.annotationId
              });
            } else {
              console.error(`annotation ${action.annotationId} not found in annotation frame span`);
            }
            return;
          }
        }
        set(dispatchAtom, {
          type: EVENTS.SELECT_ANNOTATION,
          annotationId: mapSelectedAnnotationId ? mapSelectedAnnotationId(action.annotationId) : action.annotationId
        });
        return;
      }
    case 'SELECT_FILTER':
      {
        set(selectedFilterAtom, action.labelTitle);
        return;
      }
    case SIDE_PANEL_ACTIONS.DELETE_ANNOTATION_FRAME_SPAN:
      {
        return set(dispatchAtom, deleteAnnotationFrameSpan(action.annotationIdx, action.frameSpan));
      }
    case SIDE_PANEL_ACTIONS.APPLY_FRAME_CLASSIFICATION_SPAN:
      {
        set(dispatchAtom, {
          type: UNDOABLE_EVENTS.APPLY_FRAME_CLASSIFICATION_SPAN,
          annotationId: action.annotationIdx,
          annotationLabel: action.annotationLabel,
          answers: action.answers,
          newSpan: action.frameSpan
        });
        return;
      }
    case 'ADD_INSTANCE_LABEL':
      {
        const annotationLabels = get(annotationLabelsAtom);
        const selectedLabel = annotationLabels.find(label => label.title === action.labelName);
        if (!selectedLabel) {
          console.error('Invalid label is being sent for creating instance');
          return;
        }
        const lastInstanceCount = getLastInstanceCount(selectedLabel.title, annotationLabels.map(label => label.title));
        const color = getRandomRGBColor();
        const newLabel: AnnotationLabelT = {
          ...selectedLabel,
          title: `${selectedLabel.title}_${lastInstanceCount + 1}`,
          style: {
            ...selectedLabel.style,
            fillColor: color,
            strokeColor: color
          }
        };
        const nextAnnotationLabels = [...annotationLabels, newLabel];
        set(annotationLabelsAtom, nextAnnotationLabels);
        set(dispatchAtom, {
          type: EVENTS.UPDATE_ALL_ANNOTATION_LABELS,
          annotationLabels: nextAnnotationLabels
        });
        return;
      }
    case SIDE_PANEL_ACTIONS.IMPORT_ML_ANNOTATIONS:
      {
        set(dispatchAtom, importMLAnnotationsAction({
          frameIndex: currentFrame,
          annotationIdList: action.annotationIdList,
          reviewAction: action.reviewAction
        }));
        return;
      }
    default:
      {
        set(dispatchAtom, action);
      }
  }
});